import React, { PropsWithChildren, useState } from 'react'
import { Bars3Icon } from '@heroicons/react/24/solid'
import { Head, usePage } from '@inertiajs/react'
import FlashMessages from '@/Components/Feedback/FlashMessages'
import MobileSidebar from '@/Layouts/Partials/MobileSidebar'
import { ChangeClientUrl, Navigation, PageProps } from '@/types'
import { useNavigation } from '@/Layouts/Partials/SidebarNavigation/useNavigation'
import { DesktopSidebar } from '@/Layouts/Partials/SidebarNavigation/DesktopSidebar'
import { UserMenuTop } from '@/Layouts/Partials/UserMenu'
import { classNames } from '@/Utils/Utils'
import { Link } from '@/Components/Navigation/Link'
import Tippy from '@/Components/Feedback/Tippy'
import { QuestionMarkCircleIcon } from '@heroicons/react/20/solid'
import { RightSidebarProvider, useRightSidebar } from '@/Layouts/RightSidebarContext'
import { RightSidebarPanel } from '@/Layouts/Partials/RightSidebarPanel'

export default function AuthenticatedLayout({
    pageTitle = '',
    changeClientUrl,
    children,
}: PropsWithChildren<{
    pageTitle?: string
    changeClientUrl?: ChangeClientUrl
}>) {
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const user = usePage<PageProps>().props.auth.user
    const navigation = useNavigation(changeClientUrl)

    return (
        <RightSidebarProvider>
            <Head title={pageTitle} />

            <div className="min-h-screen lg:h-screen lg:overflow-hidden">
                <MobileSidebar
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                    navigation={navigation}
                    user={user}
                    changeClientUrl={changeClientUrl}
                />
                <DesktopSidebar user={user} navigation={navigation} changeClientUrl={changeClientUrl} />

                <ContentWrapper>
                    <MobileTopBar user={user} setSidebarOpen={setSidebarOpen} navigation={navigation} />

                    <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-12">{children}</main>
                </ContentWrapper>
            </div>

            <RightSidebarPanel />
            <FlashMessages />
        </RightSidebarProvider>
    )
}

export function PageTitle({
    children,
    title,
    subtitle,
    titleHelpText,
}: PropsWithChildren<{
    title: string | React.ReactNode
    subtitle?: string | React.ReactNode
    titleHelpText?: string | React.ReactNode
}>) {
    return (
        <div className="min-h-9 sm:mb-6 sm:flex sm:items-center">
            <div className="sm:flex-auto">
                {titleHelpText ? (
                    <div className={'flex items-center space-x-1'}>
                        <h1 className="text-xl font-semibold">{title}</h1>
                        <Tippy content={titleHelpText} keepOpenOnHover={true}>
                            <QuestionMarkCircleIcon className={'h-5 w-5 text-stone-500'} />
                        </Tippy>
                    </div>
                ) : (
                    <h1 className="text-xl font-semibold">{title}</h1>
                )}

                {subtitle && <p className="text-sm text-gray-600">{subtitle}</p>}
            </div>

            {children && <div className={'mt-3 mb-4 sm:mt-0 sm:mb-0'}>{children}</div>}
        </div>
    )
}

function ContentWrapper({ children }: PropsWithChildren) {
    const { isOpen } = useRightSidebar()

    return (
        <div
            scroll-region=""
            className={`transition-[margin-right] duration-300 lg:h-screen lg:overflow-y-auto lg:pl-72 ${isOpen ? 'lg:mr-[420px]' : ''}`}
        >
            {children}
        </div>
    )
}

const PageSectionContext = React.createContext<{ plain?: boolean }>({
    plain: false,
})

export function PageSection({ children, className, plain, id }: PropsWithChildren<{ className?: string; plain?: boolean; id?: string }>) {
    return (
        <PageSectionContext.Provider value={{ plain } as React.ContextType<typeof PageSectionContext>}>
            <div id={id} className={classNames(className, 'bg-white shadow sm:rounded-lg', !plain && 'p-4 sm:p-8')}>
                {children}
            </div>
        </PageSectionContext.Provider>
    )
}

export function PageSectionHeader({
    className,
    title,
    subtitle,
    children,
    href,
}: PropsWithChildren<{
    className?: string
    title?: string | React.ReactNode
    subtitle?: string | boolean
    href?: string
}>) {
    let { plain } = React.useContext(PageSectionContext)

    const heading = <PageSectionHeaderHeading>{title}</PageSectionHeaderHeading>

    return (
        <div className={classNames(className, plain ? 'p-4 sm:p-8' : 'pb-6')}>
            {href ? <Link href={href}>{heading}</Link> : heading}

            {subtitle && <p className={'mt-4 max-w-2xl text-base text-stone-700'} dangerouslySetInnerHTML={{ __html: subtitle }} />}

            {children}
        </div>
    )
}

export function PageSectionHeaderHeading({ children }: PropsWithChildren) {
    return <h2 className="inline-block text-lg leading-tight font-bold">{children}</h2>
}

function MobileTopBar({
    user,
    setSidebarOpen,
    navigation,
}: {
    user: { email: string }
    setSidebarOpen: (open: boolean) => void
    navigation: Navigation
}) {
    return (
        <div className="flex h-16 shrink-0 items-center justify-between gap-x-4 border-b border-stone-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:hidden lg:px-8">
            <button type="button" onClick={() => setSidebarOpen(true)} className="-m-2.5 p-2.5 text-stone-700">
                <span className="sr-only">Open sidebar</span>
                <Bars3Icon aria-hidden="true" className="h-6 w-6" />
            </button>

            <UserMenuTop user={user} userNavigation={navigation.sections.user} />
        </div>
    )
}
